const DB_NAME = "ChartDataDB";
const DB_VERSION = 6;
const STORE_NAME = "chartData";
const MARKER_STORE = "fetalMarkers";


const normalizeToSeconds = (timestamp) => {
  if (!timestamp) return 0;
  const ts = Number(timestamp);
  return String(ts).length === 13 ? Math.floor(ts / 1000) : ts;
};

const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, {
          keyPath: "patientID",
        });

        objectStore.createIndex("lastUpdated", "lastUpdated", {
          unique: false,
        });
        objectStore.createIndex("sessionStartTime", "sessionStartTime", {
          unique: false,
        });
      }
      if (!db.objectStoreNames.contains(MARKER_STORE)) {
    db.createObjectStore(MARKER_STORE, {
      keyPath: "patientID",
    });
  }
    };
  });
};

export const saveToIndexedDB = async (
  patientID,
  keyarData,
  sessionStartTime,
) => {
  try {
    if (!patientID || !keyarData?.params) {
      return;
    }

    const db = await initDB();
    const now = Date.now() / 1000;
    const sessionStartSec = normalizeToSeconds(sessionStartTime);

    const readTransaction = db.transaction([STORE_NAME], "readonly");
    const readStore = readTransaction.objectStore(STORE_NAME);
    const getRequest = readStore.get(patientID);

    const existingData = await new Promise((resolve) => {
      getRequest.onsuccess = () => resolve(getRequest.result);
      getRequest.onerror = () => resolve(null);
    });

    const filteredParams = keyarData.params.map((param) => {
      const existingParam = existingData?.params?.find(
        (p) => p.param_type === param.param_type,
      );

      const mergedDataSet = {
        ...(existingParam?.data_set || {}),
        ...(param.data_set || {}),
      };

      const filteredDataSet = {};
      Object.entries(mergedDataSet).forEach(([timestamp, value]) => {
        const ts = normalizeToSeconds(timestamp);
        if (ts >= sessionStartSec) {
          filteredDataSet[timestamp] = value;
        }
      });

      return {
        ...param,
        data_set: filteredDataSet,
      };
    });

    const dataToSave = {
      patientID: patientID,
      params: filteredParams,
      sessionStartTime: sessionStartSec,
      firstDataTimestamp: (() => {
        const fhr = filteredParams.find((p) => p.param_type === "fhr");
        const keys = Object.keys(fhr?.data_set || {});
        return keys.length > 0
          ? normalizeToSeconds(
              Math.min(...keys.map((k) => normalizeToSeconds(k))),
            )
          : null;
      })(),
      lastUpdated: now,
    };

    const writeTransaction = db.transaction([STORE_NAME], "readwrite");
    const writeStore = writeTransaction.objectStore(STORE_NAME);
    const putRequest = writeStore.put(dataToSave);

    return new Promise((resolve, reject) => {
      putRequest.onsuccess = () => {
        // console.log(
        //   `[${patientID}] 💾 Saved ${
        //     Object.keys(filteredParams[0]?.data_set || {}).length
        //   } points to IndexedDB`
        // );
        resolve();
      };
      putRequest.onerror = () => {
        console.error(`[${patientID}] ❌ Failed to save:`, putRequest.error);
        reject(putRequest.error);
      };
    });
  } catch (error) {
    // console.error(`[${patientID}] ⚠️ IndexedDB save error:`, error);
  }
};

export const loadFromIndexedDB = async (patientID, currentSessionStartTime) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(patientID);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const data = request.result;

        if (!data) {
          // console.log(`[${patientID}] 📂 No cached data found`);
          resolve(null);
          return;
        }

        const now = Date.now() / 1000;
        const oneAndHalfHoursAgo = now - 90 * 60;

        if (data.lastUpdated < oneAndHalfHoursAgo) {
          // console.log(
          //   `[${patientID}] 🗑️ Cached data too old (>1.5h), removing`
          // );
          deleteFromIndexedDB(patientID);
          resolve(null);
          return;
        }

        if (data.sessionStartTime && currentSessionStartTime) {
          const cachedSessionSec = normalizeToSeconds(data.sessionStartTime);
          const currentSessionSec = normalizeToSeconds(currentSessionStartTime);

          if (cachedSessionSec !== currentSessionSec) {
            deleteFromIndexedDB(patientID);
            resolve(null);
            return;
          }
        }
        // console.log(
        //   `[${patientID}] 📂 Loaded from IndexedDB (${
        //     Object.keys(data.params[0]?.data_set || {}).length
        //   } points, session validated ✅)`,
        // );
        resolve(data);
      };

      request.onerror = () => {
        console.error(`[${patientID}] ❌ Failed to load:`, request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error(`[${patientID}] ⚠️ IndexedDB load error:`, error);
    return null;
  }
};

// ✅ Delete specific patient data
export const deleteFromIndexedDB = async (patientID) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(patientID);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        // console.log(`[${patientID}] 🗑️ Deleted from IndexedDB`);
        resolve();
      };
      request.onerror = () => {
        console.error(`[${patientID}] ❌ Failed to delete:`, request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error(`[${patientID}] ⚠️ IndexedDB delete error:`, error);
  }
};

// ✅ Cleanup old data (1.5 hours)
export const cleanupOldIndexedDB = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index("lastUpdated");

    const now = Date.now() / 1000;
    const oneAndHalfHoursAgo = now - 90 * 60;

    const request = index.openCursor();

    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result;

        if (cursor) {
          const data = cursor.value;

          if (data.lastUpdated < oneAndHalfHoursAgo) {
            const deleteRequest = cursor.delete();
            deleteRequest.onsuccess = () => {
              // console.log(`🗑️ Cleaned up old cache (>1.5h): ${data.patientID}`);
            };
          }

          cursor.continue();
        } else {
          // console.log("✅ Cleanup complete");
          resolve();
        }
      };

      request.onerror = () => {
        console.error("❌ Cleanup failed:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("⚠️ IndexedDB cleanup error:", error);
  }
};

export const getAllPatientIDs = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAllKeys();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        console.log("📋 All stored patient IDs:", request.result);
        resolve(request.result);
      };
      request.onerror = () => {
        console.error("❌ Failed to get keys:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("⚠️ IndexedDB getAllKeys error:", error);
    return [];
  }
};

export const clearAllIndexedDB = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        console.log("🗑️ All IndexedDB data cleared");
        resolve();
      };
      request.onerror = () => {
        console.error("❌ Failed to clear:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("⚠️ IndexedDB clear error:", error);
  }
};

export const saveFetalMarkers = async (patientID, markers) => {
  if (!patientID) return;

  const db = await initDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction([MARKER_STORE], "readwrite");
    const store = tx.objectStore(MARKER_STORE);

    const getReq = store.get(String(patientID));

    getReq.onsuccess = () => {
      const existing = getReq.result?.markers || {
        manual: {},
        automated: {},
      };

      const merged = {
        manual: { ...existing.manual, ...markers.manual },
        automated: { ...existing.automated, ...markers.automated },
      };

      store.put({
        patientID: String(patientID),
        markers: merged,
        updated: Date.now(),
      });

      resolve(true);
    };

    getReq.onerror = () => reject(getReq.error);
  });
};

export const loadFetalMarkers = async (patientID) => {
  const db = await initDB();

  return new Promise((resolve) => {
    const tx = db.transaction([MARKER_STORE], "readonly");
    const store = tx.objectStore(MARKER_STORE);

    const req = store.get(String(patientID));

    req.onsuccess = () => {
      resolve(
        req.result?.markers || {
          manual: {},
          automated: {},
        },
      );
    };

    req.onerror = () =>
      resolve({
        manual: {},
        automated: {},
      });
  });
};

export const deleteFetalMarkers = async (patientID) => {
  if (!patientID) return;

  const db = await initDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction([MARKER_STORE], "readwrite");
    const store = tx.objectStore(MARKER_STORE);

    const req = store.delete(String(patientID));

    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
};
